import _regeneratorRuntime from "@babel/runtime/regenerator";
import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { NotificationTemplate } from "@components/atoms";
import { ServiceWorkerContext, ServiceWorkerProvider } from "@components/containers";
import { SaleorProvider, useAuth, WishlistProvider } from "@sdk/react";
import { defaultTheme, GlobalStyle } from "@styles";
import { defaultDataIdFromObject, InMemoryCache } from "apollo-cache-inmemory";
import { persistCache } from "apollo-cache-persist";
import { LanguageCodeEnum } from "gqlTypes/globalTypes";
import * as React from "react";
import { positions, Provider as AlertProvider, useAlert } from "react-alert";
import { ApolloProvider } from "react-apollo";
import { render } from "react-dom";
import { hot } from "react-hot-loader";
import { Route, Router } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { QueryParamProvider } from "use-query-params";
import { usePreferences } from "./@next/hooks";
import { createSaleorClient } from "./@sdk";
import { authLink, fireSignOut, invalidTokenLinkWithTokenHandler } from "./@sdk/auth";
import { App } from "./app";
import { OverlayProvider } from "./components";
import { LocaleProvider } from "./components/Locale";
import { PreferencesProvider } from "./components/Preferences";
import ShopProvider from "./components/ShopProvider";
import { apiUrl, defaultLocale, serviceWorkerTimeout } from "./constants";
import { history } from "./history";
var cache = new InMemoryCache({
  dataIdFromObject: function dataIdFromObject(obj) {
    if (obj.__typename === "Shop") {
      return "shop";
    }

    return defaultDataIdFromObject(obj);
  }
});

var startApp =
/*#__PURE__*/
function () {
  var _ref = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee() {
    var notificationOptions, ApolloClentInvalidTokenLinkAdapter, Root;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return persistCache({
              cache: cache,
              storage: window.localStorage
            });

          case 2:
            notificationOptions = {
              position: positions.BOTTOM_RIGHT,
              timeout: 2500
            };
            /**
             * This is temporary adapter for queries and mutations not included in SDK to handle invalid token error for them.
             * Note, that after all GraphQL queries and mutations will be replaced by SDK methods, this adapter is going to be removed.
             */

            ApolloClentInvalidTokenLinkAdapter = function ApolloClentInvalidTokenLinkAdapter(_ref2) {
              var children = _ref2.children;

              var tokenExpirationCallback = function tokenExpirationCallback() {
                fireSignOut(apolloClient);
              };

              var _invalidTokenLinkWith = invalidTokenLinkWithTokenHandler(tokenExpirationCallback),
                  invalidTokenLink = _invalidTokenLinkWith.link;

              var apolloClient = React.useMemo(function () {
                return createSaleorClient(apiUrl, invalidTokenLink, authLink, cache);
              }, []);
              return children(apolloClient);
            };

            Root = hot(module)(function () {
              var Notifications = function Notifications() {
                var alert = useAlert();

                var _React$useContext = React.useContext(ServiceWorkerContext),
                    updateAvailable = _React$useContext.updateAvailable;

                React.useEffect(function () {
                  if (updateAvailable) {
                    alert.show({
                      actionText: "Refresh",
                      content: "To update the application to the latest version, please refresh the page!",
                      title: "New version is available!"
                    }, {
                      onClose: function onClose() {
                        window.location.reload();
                      },
                      timeout: 0,
                      type: "success"
                    });
                  }
                }, [updateAvailable]);
                useAuth(function (authenticated) {
                  if (authenticated) {
                    alert.show({
                      title: "You are now logged in"
                    }, {
                      type: "success"
                    });
                  } else {
                    alert.show({
                      title: "You are now logged out"
                    }, {
                      type: "success"
                    });
                  }
                });
                return null;
              };

              var _usePreferences = usePreferences(),
                  locale = _usePreferences.preferences.locale;

              return React.createElement(Router, {
                history: history
              }, React.createElement(QueryParamProvider, {
                ReactRouterRoute: Route
              }, React.createElement(ApolloClentInvalidTokenLinkAdapter, null, function (apolloClient) {
                return apolloClient && React.createElement(ApolloProvider, {
                  client: apolloClient
                }, React.createElement(SaleorProvider, {
                  client: apolloClient,
                  config: {
                    languageCode: LanguageCodeEnum[locale]
                  }
                }, React.createElement(ShopProvider, null, React.createElement(OverlayProvider, null, React.createElement(WishlistProvider, null, React.createElement(App, null), React.createElement(Notifications, null))))));
              })));
            });
            render(React.createElement(ThemeProvider, {
              theme: defaultTheme
            }, React.createElement(AlertProvider, _extends({
              template: NotificationTemplate
            }, notificationOptions), React.createElement(ServiceWorkerProvider, {
              timeout: serviceWorkerTimeout
            }, React.createElement(PreferencesProvider, {
              defaultLocale: defaultLocale
            }, React.createElement(LocaleProvider, null, React.createElement(GlobalStyle, null), React.createElement(Root, null)))))), document.getElementById("root")); // Hot Module Replacement API

            if (module.hot) {
              module.hot.accept();
            }

          case 7:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function startApp() {
    return _ref.apply(this, arguments);
  };
}();

startApp();